import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Store from '@apply/store';
import { alertModal, loginAlertModal } from '@apply/ui';
import { Header } from '../Header';
import { useAuthentication } from '../LoginPage';
import LoadPanel from 'devextreme-react/load-panel';
import { FeatureType } from '@apply/constants';
import { PrincipalExperiencePropertyDetailModal } from '../experience/modals/PrincipalExperiencePropertyDetailModal';
import ExperienceUnableToVerifyGrid from './ExperienceUnableToVerifyGrid';
import ExperienceActiveGrid from './ExperienceActiveGrid';
import ExperienceUserRecommendationPanel from './ExperienceUserRecommendationPanel';
import { HomePageLink } from '../HomePageLink';

export interface ExperiencePageProps {
    actions?: Actions;
}

export interface Actions {
    getUserExperiences?: () => Promise<void>;
}

const getUserExperience = async (id) => await Store.CrossStore.getUserExperienceItem(id);

export const ExperiencePage: React.FC<ExperiencePageProps> = ({ actions }) => {
    const { isRegisteredUser, authentication, logout, isFeatureEnabled } = useAuthentication();
    const [loadError, setLoadError] = useState<string>('');

    const [userExperience, set_userExperience] = useState<Store.UserStore.Types.UserExperience>(null);
    const [experiencePropertyOpen, set_experiencePropertyOpen] = useState<boolean>(false);
    const [initialExperienceCreate, set_initialExperienceCreate] = useState<boolean>(false);

    const [loadingOpen, set_loadingOpen] = useState<boolean>(false);
    const [loadingMessage] = useState<string>('Loading...');

    const thisLocation = useLocation();
    const navigate = useNavigate();

    const loadAllExperiences = async () => {
        await actions
            .getUserExperiences()
            // eslint-disable-next-line
            .then((a) => {
                set_loadingOpen(false);
            })
            .catch((err) => {
                setLoadError(err);
                set_loadingOpen(false);
            });
    };

    const onClickAddMoreInformation = async (id: number) => {
        const experience = await getUserExperience(id);
        set_userExperience(experience);
        set_initialExperienceCreate(false);
        set_experiencePropertyOpen(true);
    };

    const onClickAddExperienceProperty = async (id: number) => {
        const experience = await getUserExperience(id);
        set_userExperience(experience);
        set_initialExperienceCreate(true);
        set_experiencePropertyOpen(true);
    };

    const onExperiencePropertyClose = async () => {
        set_experiencePropertyOpen(false);
        loadAllExperiences();
    };

    useEffect(() => {
        if (authentication?.user?.userId && actions?.getUserExperiences) {
            set_loadingOpen(true);
            setLoadError('');
            loadAllExperiences();
        }
    }, [authentication?.user?.userId, actions]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${thisLocation.pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, thisLocation.pathname]);

    if (!isRegisteredUser || !authentication?.username) {
        return (
            <LoadPanel
                hideOnOutsideClick={false}
                visible={true}
                message={'Loading...'}
            />
        );
    }

    if (!isFeatureEnabled(FeatureType.Experience)) {
        window.location.href = '/home';
    }

    return (
        <div className='app-container home-page-container'>
            <Header />
            <main>
                <HomePageLink />
                <h2>My Experience</h2>
                <section className='create-new-experience'>
                    <div>
                        <div style={{ float: 'left' }}>
                            <button
                                className='button experienceNewPropertyButton'
                                // eslint-disable-next-line
                                onClick={(event) => {
                                    onClickAddExperienceProperty(0);
                                }}
                                style={{ marginLeft: 5, marginRight: 20 }}
                            >
                                Add Property
                            </button>
                        </div>
                        {!loadingOpen && <ExperienceUserRecommendationPanel />}
                    </div>
                </section>
                {!loadingOpen && (
                    <>
                        <ExperienceActiveGrid onClickAddMoreInformation={onClickAddMoreInformation} />
                        <ExperienceUnableToVerifyGrid />
                    </>
                )}
            </main>
            <PrincipalExperiencePropertyDetailModal
                experience={userExperience}
                isOpen={experiencePropertyOpen}
                initialExperienceCreate={initialExperienceCreate}
                onCloseCallback={onExperiencePropertyClose}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </div>
    );
};
// eslint-disable-next-line
export const mapStateToProps = (state: Store.ApplyRootStateType) => ({});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            getUserExperiences: () => dispatch(Store.CrossStore.getUserExperiences()),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(ExperiencePage);
